/**
 * LIVE per-port view -- types for GET /devices/{serial}/ports/live and the pure wording logic the
 * port table renders (services/live_ports on the controller).
 *
 * WHY (owner, 2026-10-12, JH-BasementTV EAP104 on Shasta 3.4): the Ports section showed static
 * tiles ("WAN 1 Trunk uplink / LAN 1-4 Untagged device config") -- "I cannot see the ports
 * properly like the clients or the VLANs configured". LAN 1 had moved ~154 GB to the basement TV
 * and nothing said so. This view answers, per physical port: is the link up and how fast, what is
 * the port configured to carry (untagged / tagged VLANs, routed LAN + DHCP, bridged into the
 * uplink), who is on it (clients / MACs / LLDP neighbour), and how much traffic.
 *
 * HONESTY RULES encoded here (and pinned by livePorts.test.ts):
 *   - every number says where it came from and how old it is ("from state reported 42 s ago");
 *   - "not reported" is never rendered as "none": a firmware that does not attribute clients to
 *     a port gets "on LAN 1-4 (port not reported)", not an empty Connected cell;
 *   - a rate is only shown from two samples; one sample shows counters only.
 *
 * One response shape for APs AND switches, so one component renders both.
 * Pure and dependency-free (runs under `node --test`).
 */

export type LivePortRole = "uplink" | "bridged" | "lan" | "port";
export type LivePortMode =
  | "trunk"
  | "untagged"
  | "routed"
  | "access"
  | "unconfigured"
  | "unknown";

export interface LiveClient {
  mac: string;
  ip: string | null;
  ipv6?: string[];
  hostname: string | null;
  vendor: string | null;
  /** Switch only: VLANs the MAC was learned on. */
  vlans?: number[];
  seen_seconds_before_report: number | null;
  last_seen_at: string | null;
  source: string;
  /** port = firmware named the port; interface = placed by subnet in a LAN group; none = unknown. */
  attribution: "port" | "interface" | "none";
  /** When the firmware's attribution was rejected (port had no link), what it claimed. */
  reported_port?: string;
  reason?: string;
  /** Set when the MAC is a Meridian-managed device in the same org. */
  device: { serial: string; hostname: string; kind: string | null } | null;
}

export interface LiveLldp {
  system_name: string | null;
  description: string | null;
  model: string | null;
  port: string | null;
  mgmt_ip: string | null;
  chassis_mac: string | null;
  capabilities: string[];
  /** The neighbour's own VLAN advertisement for the far end of this link. */
  vlans: { vlan: number; name: string | null; native: boolean }[];
  device?: { serial: string; hostname: string; kind: string | null } | null;
}

export interface LivePortConfig {
  mode: LivePortMode;
  role: LivePortRole;
  interface: string | null;
  untagged: { vlan: number | null; network: string | null; source?: string } | null;
  tagged: { vlan: number; name?: string | null; seen?: boolean; interface: string | null }[];
  routed: {
    subnet: string;
    dhcp: { first: number; count: number; lease_time: string | null } | null;
    live_addresses?: string[];
  } | null;
  source: string | null;
  summary: string;
}

export interface LiveTraffic {
  rx_bytes: number | null;
  tx_bytes: number | null;
  rx_errors?: number | null;
  tx_errors?: number | null;
  rx_bps: number | null;
  tx_bps: number | null;
  rate_basis: "two-samples" | null;
}

export interface LivePort {
  name: string;
  label: string;
  netdev: string | null;
  role: LivePortRole;
  role_label: string;
  link: { reported: boolean; up: boolean | null; speed_mbps: number | null; duplex: string | null; source: string | null };
  poe: { power_mw: number | null; status: string | null; fault: string | null } | null;
  config: LivePortConfig;
  /** Switch only: the current-VLANs merge (OP#1639). */
  vlans: { mode: string | null; mode_reason: string | null; tagged_known: boolean | null; seen_vlans: number[]; drift: { severity: string; message: string }[] } | null;
  clients: LiveClient[];
  /** Name of the LAN interface group whose clients may be on this port (port not reported). */
  client_group: string | null;
  lldp: LiveLldp[];
  traffic: LiveTraffic | null;
  stp: { edge: boolean | null; role: string | null; state: string | null } | null;
}

export interface LiveClientGroup {
  interface: string;
  ports: string[];
  clients: LiveClient[];
  reason: string;
}

export interface LivePortsResponse {
  serial: string;
  kind: "access_point" | "switch";
  firmware: string | null;
  firmware_family: string;
  firmware_label: string;
  port_source: string;
  source: {
    state_available: boolean;
    state_recorded_at: string | null;
    state_age_seconds: number | null;
    stale: boolean;
    samples: number;
    interval_seconds: number | null;
    config_uuid?: number | null;
    config_available?: boolean;
  };
  reports: {
    per_port_link?: boolean;
    per_port_clients?: boolean;
    lldp?: boolean;
    poe?: boolean;
    counters?: boolean;
  };
  notes: string[];
  ports: LivePort[];
  client_groups: LiveClientGroup[];
  unattributed_clients: LiveClient[];
  error?: string;
}

/** "42 s" / "3 min" / "2 h" -- compact age for the provenance line. */
export function fmtAgeShort(s: number | null | undefined): string {
  if (s == null) return "an unknown time";
  if (s < 90) return `${s} s`;
  if (s < 5400) return `${Math.round(s / 60)} min`;
  if (s < 172800) return `${Math.round(s / 3600)} h`;
  return `${Math.round(s / 86400)} d`;
}

/** The one-line provenance header: where the numbers came from and how old they are. */
export function provenanceLine(view: Pick<LivePortsResponse, "source" | "firmware_label">): string {
  const s = view.source;
  if (!s.state_available) {
    return `No state report from this device yet — showing configuration only (${view.firmware_label}).`;
  }
  const parts = [`From state reported ${fmtAgeShort(s.state_age_seconds)} ago`];
  parts.push(s.samples >= 2 ? `rates over ${s.interval_seconds ?? "?"} s` : "one sample, no rates yet");
  parts.push(view.firmware_label);
  return parts.join(" · ");
}

/** Link cell text: "1G full", "100M", "Down", "Not reported". */
export function linkLabel(link: LivePort["link"]): string {
  if (!link.reported || link.up == null) return "Not reported";
  if (!link.up) return "Down";
  const sp = link.speed_mbps;
  const speed = sp == null ? "Up" : sp >= 1000 ? `${sp / 1000}G` : `${sp}M`;
  return link.duplex && link.duplex !== "full" ? `${speed} ${link.duplex}` : speed;
}

/** StatusPill ramp for the link: up = healthy, down = unknown (a free port is not a fault). */
export function linkStatus(link: LivePort["link"]): "healthy" | "unknown" {
  return link.reported && link.up ? "healthy" : "unknown";
}

export interface VlanChip {
  text: string;
  kind: "untagged" | "tagged" | "routed" | "note";
  title: string;
}

/** The Mode & VLANs cell as chips: untagged first, then tagged, or the routed subnet. */
export function vlanChips(cfg: LivePortConfig): VlanChip[] {
  const chips: VlanChip[] = [];
  if (cfg.mode === "routed" && cfg.routed) {
    const live = (cfg.routed.live_addresses ?? []).filter((a) => a !== cfg.routed!.subnet);
    chips.push({
      text: `Routed ${cfg.routed.subnet}`,
      kind: "routed",
      title: "The AP routes (NAT) for this port" + (cfg.routed.dhcp ? " and runs a DHCP server on it" : ""),
    });
    if (cfg.routed.dhcp) {
      const d = cfg.routed.dhcp;
      chips.push({ text: `DHCP .${d.first}–.${d.first + d.count - 1}`, kind: "note", title: `Lease time ${d.lease_time ?? "default"}` });
    }
    if (live.length) {
      chips.push({ text: `Device reports ${live.join(", ")}`, kind: "note", title: "The live address differs from the configuration of record" });
    }
    return chips;
  }
  if (cfg.mode === "unconfigured") {
    return [{ text: "In no interface", kind: "note", title: cfg.summary }];
  }
  if (cfg.mode === "unknown") {
    return [{ text: "Not known", kind: "note", title: cfg.summary }];
  }
  if (cfg.untagged) {
    const v = cfg.untagged.vlan;
    chips.push({
      text: v ? `U ${v}` : "Untagged",
      kind: "untagged",
      title: v ? `Untagged (native) VLAN ${v}${cfg.untagged.network ? ` — ${cfg.untagged.network}` : ""}` : "Untagged (native) network",
    });
  }
  for (const t of cfg.tagged) {
    chips.push({ text: `T ${t.vlan}`, kind: "tagged", title: `Tagged VLAN ${t.vlan}${t.name ? ` — ${t.name}` : ""}${t.seen ? " (traffic seen)" : ""}` });
  }
  return chips;
}

/** "Trunk", "Routed LAN", "Uplink (bridged)"... the mode word under the role. */
export function modeLabel(port: LivePort): string {
  const m = port.config.mode;
  if (port.role === "bridged") return "Bridged into uplink";
  return (
    {
      trunk: "Trunk",
      untagged: "Untagged",
      routed: "Routed LAN",
      access: "Access",
      unconfigured: "Unconfigured",
      unknown: "Unknown",
    } as Record<LivePortMode, string>
  )[m];
}

/** The ports of a group as a range label: ["LAN1".."LAN4"] -> "LAN 1–4". */
export function groupLabel(ports: string[]): string {
  const nums = ports.map((p) => /^([A-Za-z]+)(\d+)$/.exec(p));
  if (nums.length > 1 && nums.every((m) => m && m[1] === nums[0]![1])) {
    const ns = nums.map((m) => Number(m![2])).sort((a, b) => a - b);
    const contiguous = ns.every((n, i) => i === 0 || n === ns[i - 1]! + 1);
    return contiguous ? `${nums[0]![1]} ${ns[0]}–${ns[ns.length - 1]}` : `${nums[0]![1]} ${ns.join(", ")}`;
  }
  return ports.join(", ");
}

/** The best name for a client: Meridian device > hostname > IP > MAC. */
export function clientName(c: LiveClient): string {
  return c.device?.hostname ?? c.hostname ?? c.ip ?? c.mac;
}

/** The best name for an LLDP neighbour. */
export function neighbourName(n: LiveLldp): string {
  return n.device?.hostname ?? n.system_name ?? n.description ?? n.chassis_mac ?? "Neighbour";
}

/**
 * The Connected cell in one line. Never "none" when the firmware simply did not say:
 *   "JH-GW-01 + 2 more" / "LLDP: ECS4150 (port 12)" / "3 on LAN 1–4 (port not reported)" /
 *   "Nothing learned" / "—" (link down, nothing known).
 */
export function connectedSummary(port: LivePort, groups: LiveClientGroup[]): string {
  const parts: string[] = [];
  if (port.lldp.length) {
    const n = port.lldp[0]!;
    parts.push(`LLDP: ${neighbourName(n)}${n.port ? ` (${n.port})` : ""}`);
  }
  if (port.clients.length) {
    const first = clientName(port.clients[0]!);
    parts.push(port.clients.length === 1 ? first : `${first} + ${port.clients.length - 1} more`);
  }
  const group = port.client_group ? groups.find((g) => g.interface === port.client_group) : undefined;
  if (!port.clients.length && group && group.clients.length && port.link.up) {
    parts.push(`${group.clients.length} on ${groupLabel(group.ports)} (port not reported)`);
  }
  if (parts.length) return parts.join(" · ");
  if (port.link.reported && !port.link.up) return "—";
  return "Nothing learned";
}

/** Clients to list when a row is expanded: the port's own, else its group's (marked). */
export function expandedClients(port: LivePort, groups: LiveClientGroup[]): { clients: LiveClient[]; shared: LiveClientGroup | null } {
  if (port.clients.length) return { clients: port.clients, shared: null };
  const group = port.client_group ? groups.find((g) => g.interface === port.client_group) ?? null : null;
  return { clients: group?.clients ?? [], shared: group };
}

/** Traffic cell: "↓ 13.4 Mbps ↑ 105 kbps" with two samples, else totals, else "Not reported". */
export function trafficLabel(t: LiveTraffic | null): string {
  if (!t) return "Not reported";
  if (t.rate_basis === "two-samples" && t.rx_bps != null && t.tx_bps != null) {
    // tx = sent OUT of this port toward the connected device: "down" from the device's view.
    return `↓ ${fmtRateShort(t.tx_bps)} ↑ ${fmtRateShort(t.rx_bps)}`;
  }
  return `${fmtBytesShort(t.tx_bytes)} out · ${fmtBytesShort(t.rx_bytes)} in`;
}

export function fmtRateShort(bps: number | null | undefined): string {
  if (bps == null) return "—";
  if (bps >= 1_000_000_000) return `${(bps / 1_000_000_000).toFixed(1)} Gbps`;
  if (bps >= 1_000_000) return `${(bps / 1_000_000).toFixed(1)} Mbps`;
  if (bps >= 1_000) return `${Math.round(bps / 1_000)} kbps`;
  return `${bps} bps`;
}

export function fmtBytesShort(n: number | null | undefined): string {
  if (n == null) return "—";
  const u = ["B", "KB", "MB", "GB", "TB"];
  let v = n;
  let i = 0;
  while (v >= 1000 && i < u.length - 1) {
    v /= 1000;
    i += 1;
  }
  return `${v.toFixed(i === 0 || v >= 10 ? 0 : 1)} ${u[i]}`;
}

/**
 * The honest empty-state line for the expanded client list, per what the firmware reports.
 */
export function clientsEmptyText(port: LivePort, view: Pick<LivePortsResponse, "source" | "reports">): string {
  if (!view.source.state_available) return "No state report yet, so nothing is known about who is on this port.";
  if (port.link.reported && !port.link.up) return "Link is down — nothing connected.";
  if (!view.reports.per_port_clients && port.role !== "uplink") {
    return "This firmware doesn't report per-port clients.";
  }
  return "No client learned on this port in the last report.";
}

/* ---- response guard (device sheet robustness, OP#1811, 2026-10-13) --------------------- */

/** Wording when the controller answered but its body is not a usable port view. */
export const LIVE_PORTS_UNAVAILABLE =
  "Live port data is unavailable: the controller's response did not include a port list.";

function arr<T>(v: unknown): T[] {
  return Array.isArray(v) ? (v as T[]) : [];
}

/**
 * Normalise a GET /devices/{serial}/ports/live body before rendering. Returns null when the
 * body has no `ports` array (an older controller, a proxy error page parsed as JSON, an empty
 * fallback body) -- the table then shows LIVE_PORTS_UNAVAILABLE instead of crashing on
 * `data.ports.find` (the e2e suite hit exactly that, 2026-10-13). Every other optional list
 * or block is defaulted, and each port missing `link` / `config` gets an honest
 * "not reported" / "unknown" block, so the cells' wording helpers never read undefined.
 */
export function normalizeLivePorts(raw: unknown): LivePortsResponse | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Partial<LivePortsResponse> & Record<string, unknown>;
  if (!Array.isArray(r.ports)) return null;
  const src = (r.source && typeof r.source === "object" ? r.source : {}) as Partial<LivePortsResponse["source"]>;
  const ports: LivePort[] = arr<Partial<LivePort>>(r.ports)
    .filter((p): p is Partial<LivePort> => !!p && typeof p === "object" && typeof p.name === "string")
    .map((p) => ({
      ...p,
      name: p.name as string,
      label: p.label ?? (p.name as string),
      netdev: p.netdev ?? null,
      role: p.role ?? "lan",
      role_label: p.role_label ?? "",
      link: p.link ?? { reported: false, up: null, speed_mbps: null, duplex: null, source: null },
      poe: p.poe ?? null,
      config: p.config
        ? { ...p.config, tagged: arr(p.config.tagged), summary: p.config.summary ?? "" }
        : { mode: "unknown", role: p.role ?? "lan", interface: null, untagged: null, tagged: [], routed: null, source: null, summary: "Configuration not reported" },
      vlans: p.vlans ?? null,
      clients: arr<LiveClient>(p.clients),
      client_group: p.client_group ?? null,
      lldp: arr<LiveLldp>(p.lldp),
      traffic: p.traffic ?? null,
      stp: p.stp ?? null,
    }));
  return {
    ...(r as LivePortsResponse),
    serial: typeof r.serial === "string" ? r.serial : "",
    kind: r.kind === "switch" ? "switch" : "access_point",
    firmware: r.firmware ?? null,
    firmware_family: r.firmware_family ?? "unknown",
    firmware_label: r.firmware_label ?? "uCentral",
    port_source: r.port_source ?? "none",
    source: {
      ...src,
      state_available: !!src.state_available,
      state_recorded_at: src.state_recorded_at ?? null,
      state_age_seconds: src.state_age_seconds ?? null,
      stale: !!src.stale,
      samples: src.samples ?? 0,
      interval_seconds: src.interval_seconds ?? null,
    },
    reports: r.reports && typeof r.reports === "object" ? r.reports : {},
    notes: arr<string>(r.notes).filter((n) => typeof n === "string"),
    ports,
    client_groups: arr<LiveClientGroup>(r.client_groups).map((g) => ({ ...g, ports: arr(g?.ports), clients: arr(g?.clients) })),
    unattributed_clients: arr<LiveClient>(r.unattributed_clients),
  };
}
